import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { templatesAPI } from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, Copy, Edit, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const response = await templatesAPI.getAll();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await templatesAPI.delete(id);
      toast.success('Template deleted successfully');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await templatesAPI.duplicate(id);
      toast.success('Template duplicated successfully');
      loadTemplates();
    } catch (error) {
      toast.error('Failed to duplicate template');
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-[#F3F4F6] mb-2">
              Templates
            </h1>
            <p className="text-lg text-[#9CA3AF]">
              Manage your email templates
            </p>
          </div>
          <Button
            onClick={() => navigate('/builder')}
            data-testid="create-template-button"
            className="bg-[#183871] hover:bg-[#1F4BA1] text-white shadow-[0_0_15px_rgba(24,56,113,0.3)]"
          >
            <Plus size={20} className="mr-2" />
            New Template
          </Button>
        </div>

        {/* Templates Grid */}
        {loading ? (
          <p className="text-[#9CA3AF] text-center py-12">Loading templates...</p>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <div 
              className="w-64 h-64 mx-auto mb-6 rounded-lg bg-[#13151A] bg-cover bg-center opacity-50"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1649513137940-daacab3ee11f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwZW1wdHklMjBzdGF0ZSUyMGlsbHVzdHJhdGlvbiUyMGJveHxlbnwwfHx8fDE3NjQ2NTgwNTB8MA&ixlib=rb-4.1.0&q=85')"
              }}
            />
            <p className="text-[#9CA3AF] mb-4">No templates yet</p>
            <Button
              onClick={() => navigate('/builder')}
              className="bg-[#183871] hover:bg-[#1F4BA1] text-white"
            >
              Create Your First Template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => (
              <div
                key={template.id}
                data-testid={`template-${template.id}`}
                className="rounded-lg border border-[#2A2D36] bg-[#13151A]/80 backdrop-blur-sm overflow-hidden shadow-[0_4px_20px_-2px_rgba(0,0,0,0.5)] hover:border-[#183871]/50 transition-colors"
              >
                {/* Preview */}
                <div className="h-48 bg-[#1F2128] border-b border-[#2A2D36] overflow-hidden">
                  <iframe
                    srcDoc={template.html}
                    className="w-full h-full pointer-events-none transform scale-50 origin-top-left"
                    title={template.name}
                    style={{ width: '200%', height: '200%' }}
                  />
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-[#F3F4F6] mb-2 truncate">
                    {template.name}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-[#9CA3AF] mb-4">
                    <Calendar size={14} />
                    <span>{format(new Date(template.created_at), 'MMM dd, yyyy')}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/builder/${template.id}`)}
                      data-testid={`edit-template-${template.id}`}
                      size="sm"
                      className="flex-1 bg-[#183871] hover:bg-[#1F4BA1] text-white"
                    >
                      <Edit size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleDuplicate(template.id)}
                      data-testid={`duplicate-template-${template.id}`}
                      size="sm"
                      variant="outline"
                      className="border-[#2A2D36] text-[#9CA3AF] hover:bg-[#1F2128] hover:text-[#F3F4F6]"
                    >
                      <Copy size={14} />
                    </Button>
                    <Button
                      onClick={() => handleDelete(template.id)}
                      data-testid={`delete-template-${template.id}`}
                      size="sm"
                      variant="outline"
                      className="border-[#2A2D36] text-[#EF4444] hover:bg-[#EF4444]/10 hover:text-[#EF4444]"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Templates;
